/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { axiosInstance } from '@coze-arch/bot-http';

export interface LibraryResourceStats {
  total: number;
  published_count: number;
  draft_count: number;
  knowledge_count: number;
  plugin_count: number;
  workflow_count: number;
  imageflow_count: number;
  prompt_count: number;
}

interface ApiWrap<T> {
  code: number;
  msg: string;
  data: T;
}

function unwrapApiData<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') {
    return undefined;
  }
  if ('code' in res && 'data' in res && (res as ApiWrap<T>).code === 0) {
    return (res as ApiWrap<T>).data;
  }
  const axiosBody = (res as { data?: ApiWrap<T> }).data;
  if (axiosBody && axiosBody.code === 0) {
    return axiosBody.data;
  }
  return undefined;
}

export async function fetchLibraryResourceStats(
  spaceId: string,
): Promise<LibraryResourceStats> {
  const res = await axiosInstance.post(
    '/api/plugin_api/library_resource_stats',
    {
      space_id: spaceId,
    },
    {
      headers: {
        'Agw-Js-Conv': 'str',
      },
    },
  );
  const data = unwrapApiData<LibraryResourceStats>(res);
  if (!data) {
    throw new Error('fetchLibraryResourceStats failed');
  }
  return data;
}
